import { useState, useEffect } from "react";
import { useMoodTracker, MoodType, moodColors } from "@/context/MoodTrackerContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { format, eachDayOfInterval, addMonths, startOfDay, getDay, isFirstDayOfMonth, startOfMonth, subMonths, isAfter, isToday } from "date-fns";
import { getLocalDateString } from "@/utils/dateUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface MoodTrackerGridProps {
  initialMonths?: number;
}

export default function MoodTrackerGrid({ initialMonths = 6 }: MoodTrackerGridProps) {
  const { getMoodForDate, setMoodForDate, deleteMoodForDate } = useMoodTracker();
  const isMobile = useIsMobile();
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const [startDate, setStartDate] = useState(() => {
    // Start from today and go backwards (like GitHub contributions)
    const today = new Date();
    return startOfMonth(subMonths(today, initialMonths - 1));
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

  // Navigate through time periods
  const navigateMonths = (direction: 'forward' | 'backward') => {
    setStartDate(prevDate => {
      // Use the same amount as the grid shows
      const navigationAmount = initialMonths;
      return direction === 'forward' 
        ? addMonths(prevDate, navigationAmount)
        : addMonths(prevDate, -navigationAmount);
    });
  };

  // Generate the dates for the grid (same structure as habit grid)
  useEffect(() => {
    // Calculate end date - show up to today only
    const today = new Date();
    // End at today, no future dates
    const endDate = today;
    
    // Generate all days in the interval
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    // Create data structure for grid
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    // Process each date and organize by day of week
    let currentMonth = "";
    let weekIndex = 0;
    
    // Fill the grid with weeks, where each column is a week
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
      
      // Check for start of new week
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      // Check for first day of month to add month label
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      // Ensure all rows have this week column
      for (let i = 0; i < 7; i++) {
        // Extend the array if needed
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      // Place the date in the grid
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, [startDate, initialMonths]);

  const handleDateClick = (date: Date | null, event: React.MouseEvent) => {
    if (!date) return;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Don't allow future dates
    if (isAfter(date, today)) return;
    
    const dateString = getLocalDateString(date);
    
    // Calculate position for dropdown
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const dropdownWidth = 120;
    const dropdownHeight = 140;
    
    // Position dropdown to the right of the cell, or left if too close to edge
    let x = rect.right + 8;
    let y = rect.top;
    
    // Adjust if dropdown would go off screen
    if (x + dropdownWidth > window.innerWidth) {
      x = rect.left - dropdownWidth - 8;
    }
    if (y + dropdownHeight > window.innerHeight) {
      y = window.innerHeight - dropdownHeight - 8;
    }
    
    setDropdownPosition({ x, y });
    setSelectedDate(dateString);
  };

  const handleMoodChange = (mood: MoodType) => {
    if (!selectedDate) return;
    
    setMoodForDate(selectedDate, mood);
    setSelectedDate(null);
  };

  const handleDeleteMood = () => {
    if (!selectedDate) return;
    
    deleteMoodForDate(selectedDate);
    setSelectedDate(null);
  };

  const getCellColor = (date: Date | null): string => {
    if (!date) return 'transparent';
    
    const dateString = getLocalDateString(date);
    const mood = getMoodForDate(dateString);
    
    if (!mood) return moodColors.Easy.empty;
    
    return moodColors[mood].filled;
  };

  const getCellTitle = (date: Date | null): string => {
    if (!date) return '';
    
    const dateString = getLocalDateString(date);
    const mood = getMoodForDate(dateString);
    
    if (!mood) return `${format(date, 'MMM d')}: Click to set mood`;
    
    return `${format(date, 'MMM d')}: ${mood}`;
  };

  const getTotalMoodCount = (): number => {
    const allDates = new Set<string>();
    gridData.forEach(row => {
      row.forEach(date => {
        if (date) {
          const dateString = getLocalDateString(date);
          if (getMoodForDate(dateString)) {
            allDates.add(dateString);
          }
        }
      });
    });
    return allDates.size;
  };

  const isDateCompleted = (date: Date | null): boolean => {
    if (!date) return false;
    const dateString = getLocalDateString(date);
    return !!getMoodForDate(dateString);
  };

  // Weekday labels
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  
  // Adjust dimensions based on mobile or desktop (same as habit grid)
  const cellRowHeight = isMobile ? "15px" : "20px";
  const weekdayColumnWidth = isMobile ? "w-4" : "w-6";
  const monthLabelLeftCalc = (index: number) => `${index * (isMobile ? 15 : 20)}px`;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const MoodGridCell = ({ date }: { date: Date | null }) => {
    if (!date) return <div className="w-4 h-4 rounded-sm opacity-0" />; // Transparent but takes space
    
    const dateStr = format(date, 'MMMM d, yyyy');
    const mood = getMoodForDate(format(date, 'yyyy-MM-dd'));
    const cellSize = isMobile ? "w-3 h-3" : "w-4 h-4";
    const isTodayDate = isToday(date);
    
    const glowEffect = mood ? "shadow-[0_0_5px_0px_rgba(255,255,255,0.3)] dark:shadow-[0_0_8px_0px_rgba(255,255,255,0.15)]" : "";
    const hoverEffect = !isAfter(date, today) ? "hover:scale-125 hover:z-10 hover:shadow-lg cursor-pointer" : "cursor-default opacity-30";
    const todayBorder = isTodayDate ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "";
    
    return (
      <div
        onClick={(e) => handleDateClick(date, e)}
        className={`${cellSize} rounded-sm transition-all duration-200 relative ${glowEffect} ${hoverEffect} ${todayBorder} habit-grid-cell`}
        style={{ 
          backgroundColor: getCellColor(date),
        }}
        title={getCellTitle(date)}
        data-date={dateStr}
        data-testid="mood-cell"
        role="button"
        tabIndex={0}
      />
    );
  };

  return (
    <div className="mood-tracker-grid-container mt-4" data-testid="mood-tracker-grid">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: moodColors.Easy.filled }} />
              <span>Easy</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: moodColors.Meh.filled }} />
              <span>Meh</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: moodColors.Struggled.filled }} />
              <span>Struggled</span>
            </div>
          </div>
          {!isMobile && (
            <span className="text-sm text-muted-foreground">
              {getTotalMoodCount()} days tracked
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            onClick={() => navigateMonths('backward')}
            className="p-1 rounded hover:bg-accent"
            aria-label="Previous months"
          >
            &larr;
          </button>
          <button 
            onClick={() => navigateMonths('forward')}
            className="p-1 rounded hover:bg-accent"
            aria-label="Next months"
          >
            &rarr;
          </button>
        </div>
      </div>

      {/* Mobile total count */}
      {isMobile && (
        <div className="text-sm text-muted-foreground mb-4">
          {getTotalMoodCount()} days tracked
        </div>
      )}
      
      {/* Grid - Same structure as HabitGridBody */}
      <div className="overflow-x-auto w-full">
        <div className="min-w-max">
          {/* Month labels - Fixed positioning and width */}
          <div className="flex h-6 relative mb-1">
            <div className={`${weekdayColumnWidth} mr-2`}></div> {/* Spacer for alignment with weekday column */}
            <div className="flex-1 relative">
              {months.map((month, i) => (
                <div 
                  key={i} 
                  className="absolute text-xs text-muted-foreground"
                  style={{ 
                    left: monthLabelLeftCalc(month.index),
                  }}
                >
                  {month.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex">
            {/* Weekday labels - Fixed width, exactly aligned with rows */}
            <div className={`flex flex-col ${weekdayColumnWidth} mr-2`}>
              {weekdays.map((day, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center text-xs text-muted-foreground leading-none mb-1"
                  style={{ height: cellRowHeight }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Grid cells */}
            <div className="flex flex-col">
              {gridData.map((row, rowIndex) => (
                <div
                  key={`row-${rowIndex}`}
                  className={`flex items-center ${isMobile ? 'gap-[2px]' : 'gap-1'} mb-1`}
                  style={{ height: cellRowHeight }}
                >
                  {row.map((date, colIndex) => (
                    <MoodGridCell
                      key={`cell-${rowIndex}-${colIndex}`}
                      date={date}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Small Dropdown at clicked position */}
      {selectedDate && (
        <>
          {/* Backdrop for closing dropdown when clicking outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setSelectedDate(null)}
          />
          
          {/* Dropdown */}
          <div 
            className="fixed z-50 bg-background border rounded-lg shadow-lg p-2 min-w-[120px]"
            style={{ 
              left: dropdownPosition.x,
              top: dropdownPosition.y
            }}
          >
            <div className="text-xs text-muted-foreground mb-2 px-1">
              {format(new Date(selectedDate), 'MMM d')}
            </div>
            
            <div className="space-y-1">
              <button
                onClick={() => handleMoodChange('Easy')}
                className="w-full flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-accent transition-colors"
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: moodColors.Easy.filled }} />
                Easy
              </button>
              
              <button
                onClick={() => handleMoodChange('Meh')}
                className="w-full flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-accent transition-colors"
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: moodColors.Meh.filled }} />
                Meh
              </button>
              
              <button
                onClick={() => handleMoodChange('Struggled')}
                className="w-full flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-accent transition-colors"
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: moodColors.Struggled.filled }} />
                Struggled
              </button>
              
              {getMoodForDate(selectedDate) && (
                <>
                  <hr className="my-1" />
                  <button
                    onClick={handleDeleteMood}
                    className="w-full px-2 py-1 text-sm text-destructive rounded hover:bg-destructive/10 transition-colors"
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
} 